import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CheckCircledIcon } from '@radix-ui/react-icons';
import { useState } from 'react';

const Index = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const plans = [
    {
      id: 1,
      name: 'Starter',
      price: '9,990',
      description: 'Tökéletes a kezdőknek',
      features: [
        'Legfeljebb 10 projekt',
        'Alapvető támogatás',
        '5 GB tárolóhely',
        'Heti jelentések',
        'Közösségi hozzáférés',
      ],
      highlighted: false,
    },
    {
      id: 2,
      name: 'Professional',
      price: '29,990',
      description: 'A legtöbb üzlethez',
      features: [
        'Korlátlan projektek',
        'Prioritás támogatás',
        '100 GB tárolóhely',
        'Napi jelentések',
        'API hozzáférés',
        'Egyéni integráció',
      ],
      highlighted: true,
    },
    {
      id: 3,
      name: 'Enterprise',
      price: 'Egyedi',
      description: 'Nagyvállalatok számára',
      features: [
        'Teljes testreszabás',
        '24/7 dedikált támogatás',
        'Korlátlan tárolóhely',
        'Valós idejű analitika',
        'Dedikált szerver',
        'SLA garantia',
      ],
      highlighted: false,
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      {/* Hero Section */}
      <section className="relative w-full min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-card to-background overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-secondary rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/3 w-72 h-72 bg-accent rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-primary mb-6 leading-tight">
            Üdvözöllek az oldalon!
          </h1>
          <p className="text-xl md:text-2xl text-foreground/80 mb-8 leading-relaxed max-w-2xl mx-auto">
            Fedezd fel a lehetőségeket, melyeket nyújtunk számodra. Csatlakozz hozzánk és kezdd el az utadat ma.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg transition-colors flex items-center justify-center gap-2">
              Kezdés →
            </button>
            <button className="px-8 py-4 bg-card hover:bg-secondary text-foreground font-semibold rounded-lg transition-colors border border-border">
              További információ
            </button>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="w-full py-20 bg-background border-t border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">
              Tervek és Csomagok
            </h2>
            <p className="text-lg text-foreground/80 max-w-2xl mx-auto">
              Válassz az ideális csomagból és kezdj el csodálatos dolgokat építeni még ma.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-lg transition-all border ${plan.highlighted
                  ? 'bg-gradient-to-br from-primary/20 to-primary/10 border-primary text-foreground shadow-2xl scale-105'
                  : 'bg-card text-foreground border-border shadow-lg'
                  }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-semibold">
                      Legkeresettebb
                    </span>
                  </div>
                )}

                <div className="p-8">
                  <h3 className={`text-2xl font-bold mb-2`}>
                    {plan.name}
                  </h3>
                  <p className={`mb-4 text-foreground/70`}>
                    {plan.description}
                  </p>

                  <div className="mb-6">
                    <span className="text-4xl font-bold text-primary">{plan.price}</span>
                    {plan.price !== 'Egyedi' && (
                      <span className='text-foreground/70'>
                        {' '}Ft/hó
                      </span>
                    )}
                  </div>

                  <button
                    className={`w-full py-3 rounded-lg font-semibold transition-colors mb-8 ${plan.highlighted
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-primary/20 text-primary hover:bg-primary/30'
                      }`}
                  >
                    Válaszd ezt
                  </button>

                  <div className="space-y-4">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <CheckCircledIcon
                          className={`w-5 h-5 flex-shrink-0 text-primary`}
                        />
                        <span className='text-foreground/80'>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="w-full py-20 bg-card border-t border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">
              Elérhetőség
            </h2>
            <p className="text-lg text-foreground/80 max-w-2xl mx-auto">
              Van kérdésed? Szívesen segítünk. Vedd fel velünk a kapcsolatot az alábbi módon.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-primary mb-6">Módok a kapcsolatfelvételhez</h3>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary">
                    <span className="text-lg font-bold text-primary-foreground">✉</span>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-primary mb-1">Email</h4>
                  <p className="text-foreground/70">info@example.com</p>
                  <p className="text-foreground/70">support@example.com</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary">
                    <span className="text-lg font-bold text-primary-foreground">☎</span>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-primary mb-1">Telefon</h4>
                  <p className="text-foreground/70">+36 1 123 4567</p>
                  <p className="text-sm text-foreground/60">Hétfőtől péntekig 9:00-17:00</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary">
                    <span className="text-lg font-bold text-primary-foreground">📍</span>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-primary mb-1">Hivatal</h4>
                  <p className="text-foreground/70">Budapest 1011</p>
                  <p className="text-foreground/70">Magyarország</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-primary mb-2">
                  Név
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Teljes neved"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-primary mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-primary mb-2">
                  Telefon
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="+36 1 234 5678"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-primary mb-2">
                  Üzenet
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder="Írj üzenetet..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 rounded-lg transition-colors"
              >
                Üzenet küldése
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;